import {AchievementsApi, ACHIEVEMENTS_HISTORY_LIMIT} from '@/api';
import {AchievementType} from '@/models';
import {AchievementsCount, GeekProfileAchievement} from '@/models/geek-profile';
import {Module} from 'vuex';
import {Actions} from './actions';
import {Getters} from './getters';
import {RootState} from './root.store';
import {Mutations} from './mutations';
import {
  AchievementHistory,
  AchievementsHistoryFilters,
  AddAchievementForm,
  GrantAchievementForm,
  EditAchievementForm,
} from '@/models/admin-panel';
import {initializeNotCollapsedList} from '@/shared';

export const SELECT_SEARCH_CHAR_THRESHOLD = 3;

export interface AchievementsState {
  allAchievements: AchievementType[];
  filteredAchievements: AchievementType[];
  geeksAchievements: GeekProfileAchievement[];
  geekAchievementsCount: AchievementsCount;
  achievementsCount: AchievementsCount;
  achievementsHistory: AchievementHistory[];
  achievementSharedFilter: AchievementType;
  pagination: {
    offset: number,
    totalCount: number,
  };
  achievementTypesPagination: {
    offset: number,
    totalCount: number,
  };
}

const achievementsApi = new AchievementsApi();

export interface LabeledAchievementType {
  label: string;
}

export const AchievementsModule: Module<AchievementsState, RootState> = {
  state: {
    allAchievements: [],
    filteredAchievements: [],
    geeksAchievements: [],
    achievementsHistory: [],
    achievementsCount: {
      achievementsCount: 0,
    },
    geekAchievementsCount: {
      achievementsCount: 0,
    },
    achievementSharedFilter: null as unknown as AchievementType,
    pagination: {
      offset: 0,
      totalCount: 0,
    },
    achievementTypesPagination: {
      offset: 0,
      totalCount: 0,
    },
  },

  mutations: {
    [Mutations.achievements.setAchievements](state, achievements: AchievementType[]) {
      state.allAchievements = achievements;
    },
    [Mutations.achievements.setAchievementTypesCount](state, achievementsCount: number) {
      state.achievementsCount.achievementsCount = achievementsCount;
    },
    [Mutations.achievements.setGeekAchievementTypesCount](state, achievementsCount: AchievementsCount) {
      state.geekAchievementsCount = achievementsCount;
    },
    [Mutations.achievements.setGeekAchievements](state, achievements: GeekProfileAchievement[]) {
      state.geeksAchievements = achievements;
    },
    [Mutations.achievements.setHistory](state, achievements: AchievementHistory[]) {
      state.achievementsHistory = achievements;
    },
    [Mutations.achievements.addHistory](state, history: AchievementHistory[]) {
      state.achievementsHistory.push(...history);
    },
    [Mutations.achievements.setFilteredAchievements](state, filteredAchievements: AchievementType[]) {
      state.filteredAchievements = filteredAchievements;
    },
    [Mutations.achievements.setAchievementTypeIdSharedFilter](state, achievementTypeSharedFilter: AchievementType) {
      state.achievementSharedFilter = achievementTypeSharedFilter;
    },
    [Mutations.achievements.resetPagination](state) {
      state.pagination.offset = 0;
      state.pagination.totalCount = 0;
    },
    [Mutations.achievements.incrementPagination](state) {
      state.pagination.offset += ACHIEVEMENTS_HISTORY_LIMIT;
    },
    [Mutations.achievements.addMoreAchievements](state, achievements: AchievementType[]) {
      state.allAchievements.push(...achievements);
    },
    [Mutations.achievements.setAchievementsCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.achievements.setAchievementsCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.achievements.resetFilteredAchievements](state) {
      state.filteredAchievements = [];
    },
  },

  getters: {
    [Getters.achievements.filtered](state) {
      return state.filteredAchievements.reduce((filtered: LabeledAchievementType[], achievement: AchievementType) => {
        filtered.push({...achievement, label: achievement.name.toLowerCase()});
        return filtered;
      }, []);
    },
  },

  actions: {
    async [Actions.achievements.fetchAllAchievements]({commit}): Promise<void> {
      const achievements = await achievementsApi.fetchAllAchievementsTypes();
      commit(Mutations.achievements.setAchievements, achievements);
    },

    async [Actions.achievements.fetchFilteredAchievements]({commit, state}, searchPhrase: string): Promise<void> {
      if (searchPhrase.length < SELECT_SEARCH_CHAR_THRESHOLD) {
        commit(Mutations.achievements.resetFilteredAchievements);
        return;
      }

      const searchPhraseLowerCase = searchPhrase.toLowerCase();
      const filteredAchievements = state.allAchievements
        .filter(x => x.name.toLowerCase().includes(searchPhraseLowerCase));

      commit(Mutations.achievements.setFilteredAchievements, filteredAchievements);
    },

    async [Actions.achievements.fetchGeekAchievementsCount]({commit}, geekId: string): Promise<void> {
      const geekAchievementsCount = await achievementsApi.fetchGeekAchievementsCount(geekId);
      commit(Mutations.achievements.setGeekAchievementTypesCount, geekAchievementsCount);
    },

    async [Actions.achievements.fetchGeekAchievements]({commit}, geekId: string): Promise<void> {
      const geekAchievements = await achievementsApi.fetchGeekAchievements(geekId);
      commit(Mutations.achievements.setGeekAchievements, geekAchievements);
    },

    [Actions.achievements.resetGeekAchievements]({commit}) {
      commit(Mutations.achievements.setGeekAchievementTypesCount, {});
      commit(Mutations.achievements.setGeekAchievements, []);
    },

    async [Actions.achievements.fetchAchievementsHistory](
      {commit},
      filters: AchievementsHistoryFilters): Promise<void> {
      commit(Mutations.achievements.resetPagination);
      const achievementsHistory = await achievementsApi.fetchAchievementsHistory(filters);
      initializeNotCollapsedList<AchievementHistory>(achievementsHistory.data);
      commit(Mutations.achievements.setHistory, achievementsHistory.data);
      commit(Mutations.achievements.setAchievementTypesCount, achievementsHistory.totalCount);
    },

    async [Actions.achievements.getAchievementTypeBasicInfo](_, achievementTypeId?: string): Promise<AchievementType> {
      const achievementTypeBasicInfo = await achievementsApi.fetchAchievementTypeBasicInfo(achievementTypeId);
      return Promise.resolve(achievementTypeBasicInfo);
    },

    async [Actions.achievements.fetchMoreAchievementsHistory](
      {commit, state},
      filters: AchievementsHistoryFilters): Promise<void> {
      commit(Mutations.achievements.incrementPagination);
      const achievementsHistory = await achievementsApi.fetchAchievementsHistory(filters, state.pagination.offset);
      initializeNotCollapsedList<AchievementHistory>(achievementsHistory.data);
      commit(Mutations.achievements.addHistory, achievementsHistory.data);
      commit(Mutations.achievements.setAchievementTypesCount, achievementsHistory.totalCount);
    },

    async [Actions.achievements.resetAchievementTypes]({commit}): Promise<void> {
      commit(Mutations.achievements.resetFilteredAchievements);
    },

    async [Actions.achievements.updateAchievementTypeIdSharedFilter](
      {commit}, achievementTypeIdSharedFilter: string) {
      commit(Mutations.achievements.setAchievementTypeIdSharedFilter, achievementTypeIdSharedFilter);
    },

    async [Actions.achievements.addAchievement](_, formData: AddAchievementForm): Promise<void> {
      return achievementsApi.addAchievement(formData);
    },

    async [Actions.achievements.grantAchievement](_, { geekId, grantData }): Promise<void> {
      return achievementsApi.grantAchievement(geekId, grantData);
    },

    async [Actions.achievements.editAchievement](_, formData: EditAchievementForm): Promise<void> {
      return achievementsApi.editAchievement(formData);
    },

    async [Actions.achievements.deleteAchievement](_, id: string): Promise<void> {
      return achievementsApi.deleteAchievement(id);
    },
  },
};
